import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import CallControl from './call-control';
import { CallScreenPop } from './call-screen-pop';
import { Box, ThemeProvider, styled } from '@mui/material';
import Draggable, { DraggableProps } from 'react-draggable';

const DraggableWithChildren = Draggable as React.ComponentType<Partial<DraggableProps> & { children?: React.ReactNode }>;
import { Scrollbars } from 'react-custom-scrollbars-2';
import { throttle } from 'lodash';
import useResizeObserver from 'use-resize-observer';
import CallWarnings from './call-warnings';
import { useDesktop } from '@revenuewell/logic-desktop';
import { usePhone } from '../../hooks/use-phone';
import { useShowWarning } from '../../hooks/use-show-warning';
import { callTheme } from '../material-theme';
import useScreenPop from '../../hooks/use-screen-pop/use-screen-pop';

const Overlay = styled(Box)({
  position: 'absolute',
  width: '100%',
  height: '0',
  overflow: 'visible'
});

export function CallList() {
  const {
    calls: {
      allCalls,
      sortedCalls: { notOnHoldCallsIds: sortedNotOnHoldCallIds, onholdCallsIds: sortedOnHoldCallIds }
    }
  } = usePhone();
  const { isDesktopApplication, showWindow, hideWindow, resizeWindow } = useDesktop();
  const { setDirty, setPristine } = useShowWarning();
  const { kazooCalls } = useScreenPop();

  const scrollbarRef = useRef<Scrollbars>(null);
  const scrollbarBoxRef = useRef<HTMLDivElement>(null);
  const scrollbarContentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const callIds = Object.keys(allCalls);
  const hasPhoneCallPops = callIds.length > 0;
  const hasScreenPops = kazooCalls.length > 0;

  const updateScrollableHeightThrotled = (hasOnHoldCalls: boolean) =>
    throttle(() => {
      const minHeight = 260;
      if (scrollbarBoxRef.current && scrollbarRef.current && scrollbarContentRef.current) {
        const rect = scrollbarBoxRef.current.getBoundingClientRect();
        const remainingHeight = Math.max(window.innerHeight - rect.top, minHeight);
        const height = hasOnHoldCalls ? Math.min(remainingHeight, scrollbarContentRef.current.scrollHeight + 20) : 0;
        scrollbarBoxRef.current.style.height = height + 'px';
        scrollbarRef.current.forceUpdate();
      }

      if (containerRef.current) {
        resizeWindow(40, containerRef.current.scrollHeight);
        setTimeout(() => {
          if (containerRef.current) resizeWindow(40, containerRef.current.scrollHeight);
        }, 25);
      }
    }, 25);

  const hasCallsonHold = sortedOnHoldCallIds.length > 0;
  const updateScrollableHeight = useCallback(updateScrollableHeightThrotled(hasCallsonHold), [hasCallsonHold]);

  const resizeObserver = useResizeObserver<HTMLDivElement>({ onResize: updateScrollableHeight });
  useResizeObserver<HTMLDivElement>({ onResize: updateScrollableHeight, ref: scrollbarContentRef });
  useResizeObserver<HTMLDivElement>({ onResize: updateScrollableHeight, ref: containerRef });

  useLayoutEffect(() => {
    updateScrollableHeight();
  }, [sortedOnHoldCallIds]);

  useEffect(() => {
    if (callIds && callIds.length && !isDesktopApplication()) setDirty();
    return () => {
      setPristine();
    };
  }, [callIds]);

  useEffect(() => {
    if (isDesktopApplication()) hasPhoneCallPops || hasScreenPops ? showWindow() : hideWindow();
  }, [hasPhoneCallPops, hasScreenPops]);

  const renderContent = () => (
    <div ref={containerRef} style={{
      position: 'absolute',
      right: '16px',
      top: '16px',
      zIndex: isDesktopApplication() ? 200 : 999
    }}>
      <CallWarnings hasCallPops={hasPhoneCallPops} hasScreenPops={hasScreenPops} />
      <div ref={resizeObserver.ref} data-testid="not-on-hold-calls-div">
        {kazooCalls.map((call) => (
          <CallScreenPop key={call.phoneNumber} phoneNumber={call.phoneNumber} />
        ))}
        {sortedNotOnHoldCallIds.map((id) => (
          <CallControl key={id} callId={id} />
        ))}
      </div>
      {/* <div>{showParkedCalls && !!parkedCallIds.length && <CallParking parkedCalls={parkedCalls} />}</div> */}
      <div ref={scrollbarBoxRef} style={{ width: 410 }}>
        <Scrollbars ref={scrollbarRef}>
          <div ref={scrollbarContentRef} data-testid="on-hold-calls-div">
            {sortedOnHoldCallIds.map((id) => (
              <CallControl key={id} callId={id} />
            ))}
          </div>
        </Scrollbars>
      </div>
    </div>
  );

  const render = () => {
    if (!isDesktopApplication()) return <DraggableWithChildren onDrag={updateScrollableHeight}>{renderContent()}</DraggableWithChildren>;
    return renderContent();
  };

  return (
    <Overlay>
      <ThemeProvider theme={callTheme}>{render()}</ThemeProvider>
    </Overlay>
  );
}
